import React, { forwardRef, MutableRefObject, useRef, useState } from 'react';
import { Global } from '@emotion/react';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import { useTranslation } from 'react-i18next';
import SelectApprovalType from '@/components/Approval/CreateApprovalModal/SelectApprovalType';
import { PAGE_URL } from '@/constant/url';
import { NATIVE_APPROVAL_TYPES } from '@/constant';
import { useDialog } from '@/hooks/global-tools';
import IframeApproval, {
  IframeApprovalMessageAction,
} from '@/components/Approval/IframeApprovalModal/IframeApproval';
import ApprovalCreate from '@/components/Approval/ApprovalCreate';
import {
  NATIVE_APPROVAL_MODAL_CLASS,
  nativeApprovalModalStyles,
} from '@/components/Approval/ApprovalCreate/styles';
import ApprovalView from '@/components/Approval/ApprovalView';
import useApprovalEditMode, {
  EditModeLoading,
} from '@/components/Approval/@hooks/useApprovalEditMode';
import { EventPageAreaName, GTMEventName } from '@/constant/gtm';
import { useWorkEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import WorkingHoursChangeModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkHours/WorkingHoursChangeModal';
import OvertimeRequestModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/Overtime/OvertimeRequestModal';
import OvertimeAdjustModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/Overtime/OvertimeAdjustModal';
import AnnualLeaveModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkHours/AnnualLeaveModal';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { isAttendanceApprovalCompanyAllowed } from '@/components/Approval/CreateApprovalModal/attendanceApproval';

type CreateApprovalModalProps = {
  /**
   * 상신(신규 작성·재상신) 성공 시 호스트가 리스트를 서버에서 다시 받아오게 하는 알림.
   * 신규 문서는 리스트에 아직 행이 없어 dataItemChange(행 수정)로는 반영할 수 없다.
   */
  onDocumentSubmitted?: (docId: string) => void;
  /** 리스트에 이미 있는 행의 상태만 고친다(회수 R 등). 행 추가는 불가. */
  dataItemChange?: (id: string, changes: Record<string, any>) => void;
};

// Web 에서만 사용 중
const CreateApprovalModal = forwardRef<ModalRef, CreateApprovalModalProps>(
  ({ onDocumentSubmitted, dataItemChange }, ref) => {
    const { t } = useTranslation();
    const pageArea = EventPageAreaName.WORK_APPROVAL;
    const eventTracker = useWorkEventTracker(pageArea);

    const dialog = useDialog();
    const [selectedApprovalType, setSelectedApprovalType] = useState(0);
    const [needCloseMessage, setNeedCloseMessage] = useState(false);
    // native 작성 제출 성공 시 모달 유지한 채 컨텐츠만 조회로 swap. null=작성 모드.
    const [submittedDocId, setSubmittedDocId] = useState<string | null>(null);
    const [isWorkingHoursChangeOpen, setIsWorkingHoursChangeOpen] =
      useState(false);

    const [isOvertimeRequestOpen, setIsOvertimeRequestOpen] = useState(false);
    const [isOvertimeAdjustOpen, setIsOvertimeAdjustOpen] = useState(false);
    const [isAnnualLeaveOpen, setIsAnnualLeaveOpen] = useState(false);
    const myInfo = useAtomValue(MyInfoState);
    const { workingHours } = myInfo;
    // 자체구현(native) 작성/편집 폼의 이탈 경고 판정 — 뒤로가기·편집 중 닫기 확인에 사용.
    const formDirtyRef = useRef<(() => boolean) | null>(null);

    const { isEditMode, setIsEditMode, initialFormState } =
      useApprovalEditMode(submittedDocId);

    const onMessage = (action: IframeApprovalMessageAction, data: any) => {
      switch (action) {
        case 'MODAL_CLOSE': {
          handleClose();
          break;
        }
        case 'ENABLE_ALERT': {
          setNeedCloseMessage(true);
          break;
        }
        case 'DISABLE_ALERT': {
          setNeedCloseMessage(false);
          break;
        }
      }
    };

    const onClose = () => {
      if (ref) {
        (ref as MutableRefObject<ModalRef>).current.close();
      }
      // 상신 시점 revalidate 는 검색 인덱스 반영 지연으로 stale 일 수 있어
      // 닫기 시점에 한 번 더 리스트를 동기화한다.
      if (submittedDocId) {
        onDocumentSubmitted?.(submittedDocId);
      }
      setSelectedApprovalType(0);
      setSubmittedDocId(null);
      setIsEditMode(false);
      setNeedCloseMessage(false);
    };

    // 법인별 사용 제한 검사. 미허용 법인이면 알럿 노출 후 false 반환(진입 차단).
    const isAttendanceApprovalAllowed = (type: number) => {
      if (!isAttendanceApprovalCompanyAllowed(type, myInfo.companyCode)) {
        dialog.show({ message: t('해당 양식을 지원하지 않는 법인입니다.') });
        return false;
      }
      return true;
    };

    const handleOpenWorkingHoursChange = () => {
      if (!isAttendanceApprovalAllowed(222)) return;
      onClose();
      setIsWorkingHoursChangeOpen(true);
    };

    const handleOpenOvertimeRequest = () => {
      if (!isAttendanceApprovalAllowed(223)) return;
      onClose();
      setIsOvertimeRequestOpen(true);
    };

    const handleOpenOvertimeAdjust = () => {
      if (!isAttendanceApprovalAllowed(224)) return;
      onClose();
      setIsOvertimeAdjustOpen(true);
    };

    const handleOpenAnnualLeave = () => {
      if (!isAttendanceApprovalAllowed(231)) return;
      onClose();
      setIsAnnualLeaveOpen(true);
    };

    const handleClose = () => {
      // 편집 중에도 submittedDocId 는 남아 있으므로 아래 단락 리턴보다 먼저 가드해야 한다.
      if (isEditMode && formDirtyRef.current?.()) {
        dialog.show({
          message: t(
            '작성화면을 닫으시겠어요?\n임시저장 되지 않은 내용은 사라집니다.',
          ),
          confirmButtonText: t('나가기'),
          onConfirm: onClose,
          cancelButtonText: t('취소'),
        });
        return;
      }
      // 제출 후 viewer 상태에서는 dirty 확인 불필요.
      if (submittedDocId) {
        onClose();
        return;
      }
      if (selectedApprovalType && (needCloseMessage || isNativeCreateForm)) {
        dialog.show({
          message: t(
            '작성화면을 닫으시겠어요?\n임시저장 되지 않은 내용은 사라집니다.',
          ),
          confirmButtonText: t('나가기'),
          onConfirm: onClose,
          cancelButtonText: t('취소'),
        });
      } else {
        onClose();
      }
    };

    // 모달은 유지한 채 양식 선택 화면으로만 복귀.
    // 작성 폼(ApprovalCreate)은 언마운트되고, 컴포넌트 밖 atom 에 남는 참조인/결재라인은
    // ApprovalCreate 의 언마운트 cleanup 이 비운다.
    const backToSelectType = () => {
      setSelectedApprovalType(0);
      setNeedCloseMessage(false);
    };

    // 뒤로가기(양식 재선택) — 닫기와 달리 모달은 그대로 두고 작성 내용만 버린다.
    // 작성 중 내용이 있을 때만 확인. native 폼은 작성 폼이 노출한 판정(formDirtyRef),
    // legacy iframe 폼은 iframe 이 보낸 ENABLE_ALERT(needCloseMessage) 기준.
    const handleBack = () => {
      const isDirty = isNativeCreateForm
        ? Boolean(formDirtyRef.current?.())
        : needCloseMessage;
      if (!isDirty) {
        backToSelectType();
        return;
      }
      dialog.show({
        message: t(
          '양식 선택으로 돌아가시겠어요?\n작성 중인 내용은 사라집니다.',
        ),
        confirmButtonText: t('나가기'),
        onConfirm: backToSelectType,
        cancelButtonText: t('취소'),
      });
    };

    const sendGTMEvent = (type: '확장해서 보기' | '축소해서 보기' | '닫기') => {
      if (!eventTracker) return;

      // GA Work - Approval 42번
      eventTracker(GTMEventName.clickPostAction, {
        area_name: 'Approval 작성',
        action_type: type,
        post_category: undefined,
        contents: undefined,
        contents_id: undefined,
        state: undefined,
      });
    };

    const isNativeType = NATIVE_APPROVAL_TYPES.includes(selectedApprovalType);
    const isNativeCreateForm = isNativeType && !submittedDocId;
    const url =
      process.env.NEXT_PUBLIC_IFRAME_URL +
      PAGE_URL.APPROVAL_CREATE(selectedApprovalType);

    return (
      <>
        <Modal
          ref={ref}
          // 자체구현 작성 폼(ApprovalCreate)일 때만 확대 너비 적용. 양식 선택/iframe 레거시는 기본 모달
          className={
            isNativeCreateForm || isEditMode
              ? NATIVE_APPROVAL_MODAL_CLASS
              : undefined
          }
          title={selectedApprovalType ? 'Approval' : t('양식을 선택해 주세요.')}
          isFixedHeight
          isExpandable
          // V2 작성 폼은 full-bleed. ApprovalModal 편집 모드와 같은 경로라 확장 시 하단 빈 띠도 함께 사라진다
          noBodyPadding={isNativeCreateForm || isEditMode}
          // 작성화면에서만 뒤로가기 노출 — 제출 완료(조회 swap) 후에는 되돌아갈 작성이 없다.
          isShowBackButton={Boolean(selectedApprovalType) && !submittedDocId}
          handleBack={handleBack}
          availableAutoClose={false}
          handleClose={handleClose}
          onClickCloseButton={() => sendGTMEvent('닫기')}
          onClickExpandButton={(isExpand: boolean) =>
            sendGTMEvent(isExpand ? '확장해서 보기' : '축소해서 보기')
          }
        >
          <>
            {(isNativeCreateForm || isEditMode) && (
              <Global styles={nativeApprovalModalStyles} />
            )}
            {!selectedApprovalType ? (
              <SelectApprovalType
                pageArea={pageArea}
                onSelect={setSelectedApprovalType}
                onOpenWorkingHoursChange={handleOpenWorkingHoursChange}
                onOpenOvertimeRequest={handleOpenOvertimeRequest}
                onOpenOvertimeAdjust={handleOpenOvertimeAdjust}
                onOpenAnnualLeave={handleOpenAnnualLeave}
              />
            ) : isNativeType ? (
              submittedDocId ? (
                // 작성 모달 그대로 두고 ApprovalView 로 swap.
                // 편집은 display:none 숨김만 — unmount 시 AI 요약 retry 가드 초기화로 over-call 됨
                <>
                  <div style={{ display: isEditMode ? 'none' : 'contents' }}>
                    <ApprovalView
                      documentId={submittedDocId}
                      apvType={selectedApprovalType}
                      onClose={handleClose}
                      onStatusChange={(status) =>
                        dataItemChange?.(submittedDocId, { status })
                      }
                      onEdit={() => setIsEditMode(true)}
                    />
                  </div>
                  {isEditMode &&
                    (initialFormState ? (
                      <ApprovalCreate
                        apvType={selectedApprovalType}
                        mode="edit"
                        documentId={submittedDocId}
                        initialFormState={initialFormState}
                        // 재상신 성공 시 모달 유지한 채 조회로 복귀 — 캐시 무효화는 제출 훅이 처리.
                        // 회수(R) 시점에 리스트에서 행이 제거돼 patch 로는 P 복원이 안 되므로
                        // 신규 상신과 동일하게 revalidate 로 반영한다.
                        onSubmitSuccess={(savedDocId) => {
                          onDocumentSubmitted?.(savedDocId);
                          setIsEditMode(false);
                        }}
                        onExit={() => setIsEditMode(false)}
                        exitGuardRef={formDirtyRef}
                      />
                    ) : (
                      <EditModeLoading />
                    ))}
                </>
              ) : (
                <ApprovalCreate
                  apvType={selectedApprovalType}
                  onSubmitSuccess={(docId) => {
                    setNeedCloseMessage(false);
                    setSubmittedDocId(docId);
                    onDocumentSubmitted?.(docId);
                  }}
                  onExit={handleClose}
                  exitGuardRef={formDirtyRef}
                />
              )
            ) : (
              <IframeApproval
                url={url}
                needCloseMessage={needCloseMessage}
                onMessage={onMessage}
              />
            )}
          </>
        </Modal>
        <WorkingHoursChangeModal
          open={isWorkingHoursChangeOpen}
          onClose={() => setIsWorkingHoursChangeOpen(false)}
          myInfo={myInfo}
          menuOnClose={() => setIsWorkingHoursChangeOpen(false)}
        />
        <OvertimeRequestModal
          open={isOvertimeRequestOpen}
          onClose={() => setIsOvertimeRequestOpen(false)}
          workingHours={workingHours ?? undefined}
        />
        <OvertimeAdjustModal
          open={isOvertimeAdjustOpen}
          onClose={() => setIsOvertimeAdjustOpen(false)}
          workingHours={workingHours ?? undefined}
        />
        <AnnualLeaveModal
          open={isAnnualLeaveOpen}
          onClose={() => setIsAnnualLeaveOpen(false)}
        />
      </>
    );
  },
);
CreateApprovalModal.displayName = 'CreateApprovalModal';

export default CreateApprovalModal;
